// @flow

import { useState, useEffect, useMemo, useCallback } from 'react'
import Cookie from 'js-cookie'

const IOS_STORE_URL = 'https://apps.apple.com/jp/app/id973447544'
const ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.spacemarket'

export const APP_LINK_URL_LOGIN = 'https://spacemarket.onelink.me/Mj3a/8bkg907n'
export const APP_LINK_URL_SIGNUP = 'https://spacemarket.onelink.me/Mj3a/612n5w21'

/**
 * @description
 * アプリリンク用のカスタムフック
 * OneLinkを使用してアプリへの遷移を行う
 * アプリがインストールされていない場合はストアへフォールバック
 *
 * 使い方:
 * const { appLinkHref, handleAppLinkClick } = useAppLink({ appLinkUrl: APP_LINK_URL_LOGIN })
 * <a href={appLinkHref} onClick={handleAppLinkClick}>開く</a>
 *
 * @param {Object} options
 * @param {string} options.appLinkUrl - 必須。設置箇所ごとのアプリリンクURL
 * @param {string} [options.afDp] - アプリ遷移先URL（deep_link_sub1とaf_dpに同値で付与）。デフォルトはwindow.location.href
 */
export const useAppLink = ({ afDp, appLinkUrl } = {}) => {
  /**
   * プロモーションリンクのCookie連携
   * cookieに入っているホストのIDと有効期限をURLパラメータとして付与
   * 「&pl_<hostId>=<timestamp>&pl_<hostId>=<timestamp>」の形式
   */
  const promotionLinkParams = useMemo(() => {
    const cookies = Cookie.get() || {}
    const promotionLinkCookies = Object.entries(cookies).filter(([cookieKey]) =>
      cookieKey.includes('sm_pl'),
    )

    if (promotionLinkCookies.length === 0) return ''

    const formattedPromotionLinkCookies = promotionLinkCookies.map(
      ([cookieKey, timestamp]) => {
        const capturingOwnerIdRegex = /^sm_pl_(?<ownerId>\d+)$/
        const found = capturingOwnerIdRegex.exec(cookieKey)
        const ownerId = found?.groups?.ownerId
        if (!ownerId) return ''
        return `&pl_${ownerId}=${String(timestamp)}`
      },
    )

    return formattedPromotionLinkCookies.join('')
  }, [])

  /**
   * アプリリンク用のURL
   * OneLinkを使用してアプリへの遷移を行う
   * AppsFlyer規約: deep_link_valueはプロジェクト識別子（"deeplink"=URLで指定された
   * アプリ内画面に遷移する正統派ディープリンクの識別子）、実データURLはdeep_link_sub1。
   * af_dpは旧バージョンアプリ互換用でdeep_link_sub1と常に同値（新旧アプリで同じ画面に
   * 着地させるため、別の値を取る実用シーンはない）。
   * deep_link_sub1とaf_dpを末尾に配置（値内に?や&を含む可能性があるためアプリ側のパース都合）
   */
  const [appLinkHref, setAppLinkHref] = useState(appLinkUrl)

  useEffect(() => {
    const deepLink = afDp ?? window.location.href
    // promotionLinkParamsが空でない場合は先頭の&を?に置き換え、空なら?から始める
    const queryStart = promotionLinkParams
      ? `?${promotionLinkParams.slice(1)}&deep_link_value=deeplink&deep_link_sub1=${deepLink}&af_dp=${deepLink}`
      : `?deep_link_value=deeplink&deep_link_sub1=${deepLink}&af_dp=${deepLink}`
    setAppLinkHref(`${appLinkUrl}${queryStart}`)
  }, [afDp, appLinkUrl, promotionLinkParams])

  /**
   * クリック時のフォールバック処理
   * - PC: 即座にストアへリダイレクト（デフォルト遷移を防止）
   * - iOS/Android: hrefでそのまま遷移
   *   - アプリあり: Universal Links / App Links でアプリ起動
   *   - アプリなし: S3のindex.htmlがUA判定してストアへリダイレクト
   */
  const handleAppLinkClick = useCallback(e => {
    if (typeof window === 'undefined') return

    const ua = window.navigator.userAgent.toLowerCase()
    const isAndroid = ua.includes('android')
    const isIOS =
      ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')
    const isMobile = isAndroid || isIOS
    const storeUrl = isAndroid ? ANDROID_STORE_URL : IOS_STORE_URL

    // PCの場合は即座にストアへリダイレクト
    if (!isMobile) {
      e.preventDefault()
      window.location.href = storeUrl
    }
  }, [])

  return { appLinkHref, handleAppLinkClick }
}
